import * as d3 from 'd3';
import { throttle } from 'lodash';
import { IntlShape } from 'react-intl';
import { assertNever } from 'src/utils/assert';
import { BrushableChartController } from 'src/utils/helpers/BaseChartController';
import { v4 as uuid } from 'uuid';
import { AnnotationViewModel } from '../annotations/AnnotationsContext';
import { GraphTooltipController } from './util/GraphTooltipController';
import { TubesCleanedGraphDataElement, TubesCleanedGraphDataSegment } from './util/types';

/**
 * Describes the various ways of representing time values in a job log.
 */
export enum LogTimeDisplay {
  /**
   * Represents the job progress in terms of real calendar time.
   * This includes the time that the controller was off, for example.
   */
  Calendar = 'CALENDAR',
  /**
   * Represents the job progress in terms of device runtime.
   * This includes only the time that the controller was actually on.
   */
  Runtime = 'RUN',
}

/** The {@link AnnotationViewModel}, but with a definitely-defined timestamp, as those are the only ones that are displayed. */
type TubesCleanedAnnotation = AnnotationViewModel & Required<Pick<AnnotationViewModel, 'timestamp'>>;

/**
 * Handles drawing the svg using an imperative API.
 */
export class TubesCleanedGraphController extends BrushableChartController {
  private readonly clipId = `clip-${uuid()}`;
  private readonly xMin: number = 0;
  private readonly yMin: number = 0;
  private xMax: number = 700;
  private readonly yMax: number = 200;
  private readonly tooltip: GraphTooltipController;
  private timeDisplay: LogTimeDisplay = LogTimeDisplay.Calendar;
  private readonly xRuntime: d3.ScaleLinear<number, number, never> = d3.scaleLinear().range([this.xMin, this.xMax]);
  private readonly xCalendar: d3.ScaleTime<number, number, never> = d3.scaleTime().range([this.xMin, this.xMax]);
  private readonly y: d3.ScaleLinear<number, number, never> = d3.scaleLinear().range([this.yMin, this.yMax]);
  private readonly xAxis: d3.Axis<d3.NumberValue> = d3.axisBottom(this.xCalendar);
  private readonly yAxis: d3.Axis<d3.NumberValue> = d3.axisLeft(this.y);

  private readonly xValueAccessor = (d: TubesCleanedGraphDataElement) =>
    this.timeDisplay === LogTimeDisplay.Calendar
      ? d.timestamp
      : this.timeDisplay === LogTimeDisplay.Runtime
        ? d.msElapsed
        : assertNever(this.timeDisplay);

  /** The rectangle element that defines the clipping mask for the graph area. */
  private readonly clipRect: d3.Selection<SVGRectElement, unknown, null, unknown>;

  /** The group element that contains all graph content. */
  private readonly rootGroup: d3.Selection<SVGGElement, unknown, null, unknown>;

  /** The group element that contains the brush selection. */
  private readonly brushGroup: d3.Selection<SVGGElement, unknown, null, unknown>;

  /** The group element that contains the x axis. */
  private readonly xAxisGroup: d3.Selection<SVGGElement, unknown, null, unknown>;

  /** The group element that contains the y axis. */
  private readonly yAxisGroup: d3.Selection<SVGGElement, unknown, null, unknown>;

  /** The group element that contains the user selection. */
  private readonly selectionGroup: d3.Selection<SVGGElement, unknown, null, unknown>;

  /** The group element that contains the annotation dots. */
  private readonly annotationGroup: d3.Selection<SVGGElement, unknown, null, unknown>;

  /** The line element for the cleaned data. */
  private readonly cleanedLine: d3.Selection<SVGPathElement, TubesCleanedGraphDataSegment, null, unknown>;

  /** The line element for the uncleaned data. */
  private readonly uncleanedLine: d3.Selection<SVGPathElement, TubesCleanedGraphDataSegment, null, unknown>;

  /**
   * The external function that is invoked whenever zoom is performed.
   * @param isModified Whether or not the current zoom level is different from its default level.
   */
  private onZoom: (isModified: boolean) => void = () => {};

  /**
   * The external function that is used to retrieve the tooltip text.
   * @param at The instant/calendar time that the event occurred.
   * @param item The associated data point with the position on the graph.
   * @param annotation The annotation associated with the data point.
   */
  private getTooltip: (at: number, item: TubesCleanedGraphDataElement, annotation?: AnnotationViewModel) => string[] =
    () => [];

  /**
   * The external function that is called whenever a new selection is made.
   * @param timestamp The calendar time (in epoch ms) that is currently selected, or undefined if the selection was cleared.
   */
  private onTimestampChange = (timestamp?: number) => {};

  /**
   * An external function that can be used to convert values between domains.
   * @param value The value to convert.
   * @param from What `value` currently represents.
   * @param to What `value` should represent.
   */
  private convertTimeValue: undefined | ((value: number, from: LogTimeDisplay, to: LogTimeDisplay) => number);

  /** The currently selected calendar time. */
  private selectedTimestamp: number | undefined;

  /** The width of the svg. */
  private get width() {
    return this.xMax - this.xMin;
  }

  /** The height of the svg. */
  private get height() {
    return this.yMax - this.yMin;
  }

  /** The viewbox of the svg. */
  private get viewBox() {
    return {
      xMin: this.xMin,
      yMin: this.yMin,
      width: this.width,
      height: this.height,
      toString: function (this) {
        return `${this.xMin} ${this.yMin} ${this.width} ${this.height}`;
      },
    };
  }

  private get x(): d3.ScaleTime<number, number, never> | d3.ScaleLinear<number, number, never> {
    switch (this.timeDisplay) {
      case LogTimeDisplay.Calendar:
        return this.xCalendar;
      case LogTimeDisplay.Runtime:
        return this.xRuntime;
      default:
        return assertNever(this.timeDisplay);
    }
  }

  /** The full domain of the graph data along the x axis. */
  private get calendarDomain(): [number, number] {
    const data = this.cleanedLine.datum();
    const result = d3.extent(data, d => d.timestamp);
    if (typeof result[0] !== 'number') return [0, 0];
    return result;
  }

  /** The full domain of the graph data along the x axis. */
  private get runtimeDomain(): [number, number] {
    const data = this.cleanedLine.datum();
    const result = d3.extent(data, d => d.msElapsed);
    if (typeof result[0] !== 'number') return [0, 0];
    return result;
  }

  /** The computed height of the x axis. */
  private get xAxisHeight() {
    return this.xAxisGroup.node()?.getBBox().height ?? 0;
  }

  /** The computed width of the y axis. */
  private get yAxisWidth() {
    return this.yAxisGroup.node()?.getBBox().width ?? 0;
  }

  private readonly cleanedLineMapper = d3
    .line<TubesCleanedGraphDataElement>()
    .curve(d3.curveStepAfter)
    .x(d => this.x(this.xValueAccessor(d)))
    .y(d => this.y(d.cleaned));

  private readonly uncleanedLineMapper = d3
    .line<TubesCleanedGraphDataElement>()
    .curve(d3.curveStepAfter)
    .x(d => this.x(this.xValueAccessor(d)))
    .y(d => this.y(d.uncleaned));

  private readonly annotationXMapper = (d: TubesCleanedAnnotation) => {
    const domainValue = this.convertTimeValue?.(d.timestamp, LogTimeDisplay.Calendar, this.timeDisplay);
    if (domainValue === undefined) return 0;
    return this.x(domainValue);
  };

  private readonly annotationYMapper =
    (accessor: (v: TubesCleanedGraphDataElement) => number) => (d: TubesCleanedAnnotation) => {
      const domainValue = this.convertTimeValue?.(d.timestamp, LogTimeDisplay.Calendar, this.timeDisplay);
      if (domainValue === undefined) return 0;
      const point = this.findDataElement(domainValue);
      if (!point) return 0;
      return this.y(accessor(point));
    };

  constructor(svg: SVGSVGElement) {
    super(svg);
    this.svg.attr('viewBox', this.viewBox.toString());
    this.svg.selectChildren().remove();

    // Create clipPath for content area. Prevents overflow during pan/zoom.
    this.clipRect = this.svg
      .append('defs')
      .append('svg:clipPath')
      .attr('id', this.clipId)
      .append<SVGRectElement>('svg:rect')
      .attr('data-id', 'clip-rect')
      .attr('x', this.viewBox.xMin)
      .attr('y', this.viewBox.yMin)
      .attr('width', this.viewBox.width)
      .attr('height', this.viewBox.height);

    // Create main content group. Will update with data in `setData`.
    this.rootGroup = this.svg
      .append('g')
      .attr('data-id', 'content')
      .attr('clip-path', `url(#${this.clipId})`)
      .append('g')
      .attr('data-id', 'root');

    // Create the tubes cleaned lines.
    this.uncleanedLine = this.rootGroup
      .append('path')
      .attr('data-id', 'uncleaned-line')
      .classed('tube-clean-line tube-clean-color-two', true)
      .datum<TubesCleanedGraphDataSegment>([]);
    this.cleanedLine = this.rootGroup
      .append('path')
      .attr('data-id', 'cleaned-line')
      .classed('tube-clean-line tube-clean-color-one', true)
      .datum<TubesCleanedGraphDataSegment>([]);

    // Create the group for brushing (allowing the user to select a region to zoom to).
    this.brushGroup = this.rootGroup.append('g').classed('graph-brush', true).attr('data-id', 'brush').call(this.brush);

    // Create group for x/y axis.
    const axisGroup = this.svg.append('g').attr('data-id', 'axis');
    this.xAxisGroup = axisGroup
      .append('g')
      .classed('x-axis graph-timestamp graph-label', true)
      .attr('data-id', 'axis-x');
    this.yAxisGroup = axisGroup.append('g').classed('y-axis graph-label', true).attr('data-id', 'axis-y');

    // Create the group for the current cursor selection.
    this.selectionGroup = this.rootGroup.append('g').attr('data-id', 'selection').classed('graph-selection', true);
    this.selectionGroup.append('line').attr('target', true).attr('y1', 0).attr('y2', this.viewBox.height);
    this.selectionGroup.append('circle').attr('target', true).attr('r', 8.5).attr('data-id', 'selection-cleaned');
    this.selectionGroup.append('circle').attr('target', true).attr('r', 8.5).attr('data-id', 'selection-uncleaned');

    this.annotationGroup = this.rootGroup.append('g').attr('data-id', 'annotations').classed('graph-annotation', true);

    this.tooltip = new GraphTooltipController(svg);

    // Bind all event listeners.
    this.brush.on('brush', this.handleBrushStart.bind(this));
    this.rootGroup
      .on('click', this.handleMouseClick.bind(this))
      .on('mouseover', this.handleMouseOver.bind(this))
      .on('mousemove', this.handleMouseMove.bind(this))
      .on('mouseout', this.handleMouseOut.bind(this));
  }

  public setTooltipAccessor(accessor: typeof this.getTooltip) {
    this.getTooltip = accessor;
  }

  public setTimeConverter(converter: typeof this.convertTimeValue) {
    this.convertTimeValue = converter;
  }

  public setOnZoom(onZoom: typeof this.onZoom) {
    this.onZoom = onZoom;
  }

  public setOnTimestampChange(callback: (v?: number) => void) {
    this.onTimestampChange = callback;
  }

  public setAspectRatio(aspectRatio: number) {
    this.xMax = Math.round(this.yMax * aspectRatio);
    this.svg.attr('viewBox', this.viewBox.toString());
    this.render();
    this.tooltip.onParentLayoutChanged(); // Inform tooltip controller that SVG has been resized.
  }

  public setDisplayConfiguration(
    intl: IntlShape,
    timeDisplay: LogTimeDisplay,
    timezone: string,
    hideTimestamp: boolean,
  ) {
    if (this.timeDisplay !== timeDisplay) {
      let nextX: number | undefined;
      if (this.selectedTimestamp !== undefined) {
        nextX = this.convertTimeValue?.(this.selectedTimestamp, LogTimeDisplay.Calendar, timeDisplay);
      }

      this.timeDisplay = timeDisplay;
      this.resetZoom(true);
      if (nextX !== undefined) this.drawSelection(nextX);
    }

    if (timeDisplay === LogTimeDisplay.Calendar) {
      const dateFormatter = new Intl.DateTimeFormat(intl.locale, {
        timeZone: timezone,
        month: 'short',
        day: 'numeric',
      });
      const timeFormatter = new Intl.DateTimeFormat(intl.locale, {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
      });

      this.xAxis.scale(this.xCalendar);
      this.xAxis.tickFormat(d => {
        const domain = this.xAxis.scale().domain();
        const span = domain[domain.length - 1].valueOf() - domain[0].valueOf();
        // If domain spans more than two days, use a date as the label instead of the time.
        if (span > 1000 * 60 * 60 * 24 * 2) {
          return dateFormatter.format(d.valueOf());
        } else {
          return timeFormatter.format(d.valueOf());
        }
      });
    } else if (timeDisplay === LogTimeDisplay.Runtime) {
      const hourFormatter = new Intl.NumberFormat(intl.locale, {
        style: 'unit',
        unit: 'hour',
        unitDisplay: 'narrow',
        maximumFractionDigits: 0,
      });
      const minuteFormatter = new Intl.NumberFormat(intl.locale, {
        style: 'unit',
        unit: 'minute',
        unitDisplay: 'narrow',
        maximumFractionDigits: 0,
      });
      const listFormatter = new Intl.ListFormat(intl.locale, { style: 'narrow', type: 'unit' });

      this.xAxis.scale(this.xRuntime);
      this.xAxis.tickFormat(d => {
        const value = d.valueOf();
        const domain = this.xAxis.scale().domain();
        const span = domain[domain.length - 1].valueOf() - domain[0].valueOf();

        /** The number of hours elapsed at the current value. */
        const hours = Math.floor(value / (1000 * 60 * 60));
        /** The number of ms remaining after the hours have been removed. */
        const remaining = value - hours * 1000 * 60 * 60;
        /** The number of minutes elapsed at the current value. */
        const minutes = Math.floor(remaining / (1000 * 60));
        // If domain spans more than a day, only show hours.
        if (span > 1000 * 60 * 60 * 24) return hourFormatter.format(hours);
        // If we're showing minutes, and there's no hours component, omit the hours.
        if (hours === 0) return minuteFormatter.format(minutes);
        // Otherwise, return both hours and minutes.
        return listFormatter.format([hourFormatter.format(hours), minuteFormatter.format(minutes)]);
      });
    } else {
      assertNever(timeDisplay);
    }

    if (hideTimestamp) {
      this.xAxis.ticks(0);
      this.xAxis.tickFormat(null);
    }

    this.render();
  }

  public onResize(entries: ResizeObserverEntry[], observer: ResizeObserver) {
    if (entries.length === 0) return;
    this.render();
    this.tooltip.onParentLayoutChanged(); // Inform tooltip controller that SVG has been resized.
  }

  public setData(data: TubesCleanedGraphDataSegment) {
    try {
      this.cleanedLine.datum(data);
      this.uncleanedLine.datum(data);

      const maxY = d3.max(data, i => Math.max(i.cleaned, i.uncleaned)) ?? 0;
      // Update y axis
      this.y.domain([0, Math.max(maxY, 10)]);

      // Update the x axis if we don't have an active selection
      if (!this.brushSelection) {
        this.xCalendar.domain(this.calendarDomain);
        this.xRuntime.domain(this.runtimeDomain);
      }

      this.yAxisGroup.call(this.yAxis);
      this.xAxisGroup.call(this.xAxis);

      this.render();
    } catch (e) {
      console.error('[TubesCleanedGraphController] Error setting data', e);
    }
  }

  public setAnnotations(annotations: AnnotationViewModel[]) {
    const toDisplay = annotations
      .filter((i): i is TubesCleanedAnnotation => i.timestamp !== undefined)
      .filter(i => !i.deletedOn);
    this.annotationGroup.selectAll('circle.cleaned').data(toDisplay).join('circle').classed('cleaned', true);
    this.annotationGroup.selectAll('circle.uncleaned').data(toDisplay).join('circle').classed('uncleaned', true);

    this.render();
  }

  public reset() {
    this.selectedTimestamp = undefined;
    this.setSelectionVisible(false);
    this.resetZoom(true);
  }

  private readonly render = throttle(
    async () => {
      // Update the text size of graph elements.
      const pixelUnit = this.getPixelUnit();
      this.xAxisGroup.style('font-size', `${pixelUnit * 14}px`);
      this.yAxisGroup.style('font-size', `${pixelUnit * 14}px`);

      // Wait for any previously applied changes to render to the screen, so we can measure.
      await new Promise(res => window.requestAnimationFrame(res));
      const xRange = this.getXAxisRange();
      const yRange = this.getYAxisRange();

      const yHeight = yRange[0] - yRange[1];
      const yTicks = Math.max(Math.ceil(yHeight / pixelUnit / 30), 2);
      this.yAxis.ticks(yTicks);

      // Determine the number of ticks to display along the x axis.
      const xWidth = xRange[1] - xRange[0];
      const xTicks = Math.max(Math.ceil(xWidth / pixelUnit / 120), 2);
      // Only update the tick count if we have a tick formatter set.
      if (!!this.xAxis.tickFormat()) this.xAxis.ticks(xTicks);

      this.y.range(yRange);
      this.yAxisGroup.call(this.yAxis);
      this.xCalendar.range(xRange);
      this.xRuntime.range(xRange);
      this.xAxisGroup.call(this.xAxis);

      // Reposition each axis.
      this.xAxisGroup.attr('transform', `translate(0, ${yRange[0]})`);
      this.yAxisGroup.attr('transform', `translate(${xRange[0]}, 0)`);

      // Update the visible charting area in the graph. This clips out any line chart data
      // below or to the left of the axis (which happens on zoom).
      this.clipRect
        .attr('x', xRange[0])
        .attr('width', xWidth + 5)
        .attr('height', yHeight + 5);

      // Set the bounds of how far horizontally we can pan.
      const extent: [[number, number], [number, number]] = [
        [xRange[0], yRange[1]],
        [xRange[1], yRange[0]],
      ];
      this.brush.extent(extent);

      // Redraw the lines to fit them inside the new graph area.
      this.cleanedLine.attr('d', this.cleanedLineMapper);
      this.uncleanedLine.attr('d', this.uncleanedLineMapper);

      // Redraw the annotations TODO:
      this.drawAnnotations();
    },
    160,
    { leading: true, trailing: true },
  );

  protected async performZoomReset(instant?: boolean): Promise<void> {
    this.onZoom(false);

    this.xCalendar.domain(this.calendarDomain);
    this.xRuntime.domain(this.runtimeDomain);

    let xAxisGroup = this.xAxisGroup;
    let cleanedLine = this.cleanedLine;
    let uncleanedLine = this.uncleanedLine;
    if (!instant) {
      xAxisGroup = xAxisGroup.transition('zoom').duration(500) as any;
      cleanedLine = cleanedLine.transition('zoom') as any;
      uncleanedLine = uncleanedLine.transition('zoom') as any;
    }
    xAxisGroup.call(this.xAxis);
    cleanedLine.attr('d', this.cleanedLineMapper);
    uncleanedLine.attr('d', this.uncleanedLineMapper);
    this.drawAnnotations(!instant ? 'zoom' : undefined);

    if (this.selectedTimestamp !== undefined) {
      const selectionX = this.convertTimeValue?.(this.selectedTimestamp, LogTimeDisplay.Calendar, this.timeDisplay);
      if (selectionX !== undefined) this.drawSelection(selectionX, !instant ? 'zoom' : undefined);
    }
  }

  protected performZoom(e: d3.D3BrushEvent<any>) {
    const extent = e.selection as [number, number] | null;
    if (!extent) return;
    const xRange = this.getXAxisRange();
    this.onZoom(this.isZoomActive);

    const range = [Math.max(extent[0], xRange[0]), Math.min(extent[1], xRange[1])];
    this.xCalendar.domain([this.xCalendar.invert(range[0]), this.xCalendar.invert(range[1])]);
    this.xRuntime.domain([this.xRuntime.invert(range[0]), this.xRuntime.invert(range[1])]);

    this.xAxisGroup.transition('zoom').duration(500).call(this.xAxis);
    this.cleanedLine.transition('zoom').attr('d', this.cleanedLineMapper);
    this.uncleanedLine.transition('zoom').attr('d', this.uncleanedLineMapper);
    this.drawAnnotations('zoom');

    if (this.selectedTimestamp !== undefined) {
      const selectionX = this.convertTimeValue?.(this.selectedTimestamp, LogTimeDisplay.Calendar, this.timeDisplay);
      if (selectionX !== undefined) this.drawSelection(selectionX, 'zoom');
    }
    this.brushGroup.call(this.brush.clear);
  }

  /** Draws the selection marker over the graph. */
  private drawSelection(at: Date | number, transition?: string) {
    const snap = this.findAnnotation(at.valueOf());
    const snapX = snap ? this.convertTimeValue?.(snap.timestamp, LogTimeDisplay.Calendar, this.timeDisplay) : undefined;
    const effX = snapX ?? at.valueOf();
    const point = this.findDataElement(effX);
    if (!point) return;

    let line = this.selectionGroup.select('line');
    let circleCleaned = this.selectionGroup.select('[data-id=selection-cleaned]');
    let circleUncleaned = this.selectionGroup.select('[data-id=selection-uncleaned]');
    if (transition) {
      line = line.transition(transition) as any;
      circleCleaned = circleCleaned.transition(transition) as any;
      circleUncleaned = circleUncleaned.transition(transition) as any;
    }
    const x = this.x(snapX ?? at);
    line.attr('x1', x).attr('x2', x);
    circleCleaned.attr('cx', x).attr('cy', this.y(point.cleaned));
    circleUncleaned.attr('cx', x).attr('cy', this.y(point.uncleaned));

    const calendarTime =
      this.selectedTimestamp ??
      snap?.timestamp ??
      this.convertTimeValue?.(at.valueOf(), this.timeDisplay, LogTimeDisplay.Calendar);
    // TODO: Get annotation
    if (calendarTime !== undefined) {
      this.tooltip.setText(this.getTooltip(calendarTime, point, snap), x, 0, transition);
    }
  }

  private drawAnnotations(transition?: string) {
    let cleaned = this.annotationGroup.selectAll<SVGCircleElement, TubesCleanedAnnotation>('circle.cleaned');
    let uncleaned = this.annotationGroup.selectAll<SVGCircleElement, TubesCleanedAnnotation>('circle.uncleaned');

    if (transition) {
      cleaned = cleaned.transition(transition) as any;
      uncleaned = uncleaned.transition(transition) as any;
    }

    cleaned
      .attr('r', 8.5)
      .attr('cx', this.annotationXMapper)
      .attr(
        'cy',
        this.annotationYMapper(d => d.cleaned),
      );

    uncleaned
      .attr('r', 8.5)
      .attr('cx', this.annotationXMapper)
      .attr(
        'cy',
        this.annotationYMapper(d => d.uncleaned),
      );
  }

  /** The range of the x axis, derived from the computed width of the y axis. */
  private getXAxisRange(): [number, number] {
    return [this.yAxisWidth + 5, this.viewBox.width - 5];
  }

  /** The range of the y axis, derived from the computed height of the x axis. */
  private getYAxisRange(): [number, number] {
    return [this.viewBox.height - this.xAxisHeight, 5];
  }

  private setSelectionVisible(isVisible: boolean) {
    this.selectionGroup.classed('visible', isVisible);
    this.tooltip.setVisible(isVisible);
  }

  private handleBrushStart() {
    if (this.selectedTimestamp !== undefined) return;
    this.setSelectionVisible(false);
  }

  private handleMouseClick(event: MouseEvent) {
    if (this.selectedTimestamp !== undefined) {
      this.selectedTimestamp = undefined;
    } else {
      const domainX = this.x.invert(d3.pointer(event, this.rootGroup.node())[0]).valueOf();
      const snap = this.findAnnotation(domainX);
      if (snap) {
        this.selectedTimestamp = snap.timestamp;
      } else {
        const calendarTime = this.convertTimeValue?.(domainX, this.timeDisplay, LogTimeDisplay.Calendar);
        this.selectedTimestamp = calendarTime;
      }
    }
    this.onTimestampChange(this.selectedTimestamp);
    this.setSelectionVisible(this.selectedTimestamp !== undefined);
  }

  private handleMouseOver() {
    if (this.selectedTimestamp !== undefined) return;
    this.setSelectionVisible(true);
  }

  private handleMouseOut() {
    if (this.selectedTimestamp !== undefined) return;
    this.setSelectionVisible(false);
  }

  private handleMouseMove(event: MouseEvent) {
    if (this.selectedTimestamp !== undefined) return;

    const rangeX = d3.pointer(event, this.rootGroup.node())[0];
    const domainX = this.x.invert(rangeX).valueOf();

    this.setSelectionVisible(true);
    this.drawSelection(domainX);
  }

  /**
   * Given a domain value, finds the closest nearby annotation object.
   * @param domainX A value in the currently active x domain.
   */
  private findAnnotation(domainX: number): TubesCleanedAnnotation | undefined {
    let snapCandidate: { data: TubesCleanedAnnotation; dist: number } | undefined;
    const rangeX = this.x(domainX);

    this.annotationGroup.selectAll<SVGCircleElement, TubesCleanedAnnotation>('circle.cleaned').each(i => {
      const x = this.convertTimeValue?.(i.timestamp, LogTimeDisplay.Calendar, this.timeDisplay);
      if (x === undefined) return;
      const candidateRangeX = this.x(x);
      const dist = Math.abs(candidateRangeX - rangeX);
      const isCloserThanExisting = !snapCandidate || dist < snapCandidate.dist;
      if (dist < 15 && isCloserThanExisting) {
        snapCandidate = { data: i, dist };
      }
    });
    return snapCandidate?.data;
  }

  /**
   * Given a domain value, finds the line data object that represents that value.
   * @param domainX A value in the currently active x domain.
   */
  private findDataElement(domainX: number): TubesCleanedGraphDataElement | undefined {
    const data = this.cleanedLine.datum();

    let i = 0;
    while (data[i + 1] && this.xValueAccessor(data[i + 1]) <= domainX) {
      i++;
    }

    return data[i];
  }
}
